import { Map, LngLat } from 'mapbox-gl';
import assert from 'assert';
import autoBind from 'auto-bind';

// Customized version of: https://github.com/mapbox/mapbox-gl-js/blob/master/src/ui/control/geolocate_control.js

interface Options {
    positionOptions?: PositionOptions;
    fitBoundsOptions?: any;
    trackUserLocation?: boolean;
    showUserLocation?: boolean;
    isAndroid?: boolean;
}

const defaultOptions: Options = {
    positionOptions: {
        enableHighAccuracy: false,
        maximumAge: 0,
        timeout: 6000 /* 6 sec */
    },
    fitBoundsOptions: {
        maxZoom: 15
    },
    trackUserLocation: false,
    showUserLocation: true,
    isAndroid: false
};

let supportsGeolocation;

function checkGeolocationSupport(callback) {
    const navigator = window.navigator as any;
    if (supportsGeolocation !== undefined) {
        callback(supportsGeolocation);
    } else if (navigator.permissions !== undefined) {
        // navigator.permissions has incomplete browser support
        // http://caniuse.com/#feat=permissions-api
        // Test for the case where a browser disables Geolocation because of an
        // insecure origin
        navigator.permissions.query({ name: 'geolocation' }).then(p => {
            supportsGeolocation = p.state !== 'denied';
            callback(supportsGeolocation);
        });
    } else {
        supportsGeolocation = !!window.navigator.geolocation;
        callback(supportsGeolocation);
    }
}

const className = 'mapboxgl-ctrl';

export default class ListoryGeolocateControl {
    // Fields ported from GeolocateControl
    private options: Options;
    private _map: Map;
    private _container: HTMLElement;
    private _geolocateButton: HTMLElement;
    private _geolocationWatchID: number;
    private _timeoutId: number;
    private _watchState: string;
    private _lastKnownPosition: any;

    // Custom fields
    private locationSource: GeoJSON.FeatureCollection;
    private locationFeature: GeoJSON.Feature;
    private locationPoint: GeoJSON.Point;
    private lastHeading = 0;
    private lastUpdate = 0;
    private useAbsoluteDeviceOrientation = false;
    private cameraUpdateDisabled = false;
    private isActive = true;

    private deviceOrientationCallback: (event: any) => void;

    constructor(options: Options) {
        this.options = Object.assign({}, defaultOptions, options);

        autoBind(this);

        const isProduction = process.env.NODE_ENV === 'production';
        this.useAbsoluteDeviceOrientation = options.isAndroid && isProduction;
    }

    public onAdd(map: Map) {
        this._map = map;

        this._container = this._createElement('div', `${className} ${className}-group`);
        checkGeolocationSupport(this._setupUI);

        this.deviceOrientationCallback = event => this.onDeviceOrientationUpdated(event);

        if (this.useAbsoluteDeviceOrientation) {
            window.addEventListener('deviceorientationabsolute', this.deviceOrientationCallback);
        } else {
            window.addEventListener('deviceorientation', this.deviceOrientationCallback);
        }

        return this._container;
    }

    public onRemove() {
        if (this._geolocationWatchID !== undefined) {
            window.navigator.geolocation.clearWatch(this._geolocationWatchID);
            this._geolocationWatchID = undefined;
        }

        this._removeElement(this._container);
        this._map = undefined;

        if (this.useAbsoluteDeviceOrientation) {
            window.removeEventListener('deviceorientationabsolute', this.deviceOrientationCallback);
        } else {
            window.removeEventListener('deviceorientation', this.deviceOrientationCallback);
        }
    }

    public setActive(active: boolean) {
        this.isActive = active;

        // disable marker, by setting position null
        if (!this.isActive) {
            this._updateMarker(null);
        }
    }

    public trigger(event: any): boolean {
        this.cameraUpdateDisabled = event === true;

        if (this.options.trackUserLocation) {
            // update watchState and do any outgoing state cleanup
            switch (this._watchState) {
                case 'OFF':
                    // turn on the Geolocate Control
                    this._watchState = 'WAITING_ACTIVE';
                    break;
                case 'WAITING_ACTIVE':
                case 'ACTIVE_LOCK':
                    // States are deactivated to prevent disabling GPS tracking
                    break;
                case 'ACTIVE_ERROR':
                case 'BACKGROUND_ERROR':
                    // turn off the Geolocate Control
                    this._watchState = 'OFF';
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-waiting');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-active');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-active-error');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background-error');
                    break;
                case 'BACKGROUND':
                    this._watchState = 'ACTIVE_LOCK';
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background');
                    // set camera to last known location
                    if (this._lastKnownPosition && !this.cameraUpdateDisabled) {
                        this._updateCamera(this._lastKnownPosition);
                    }
                    break;
                default:
                    assert(false, `Unexpected watchState ${this._watchState}`);
            }

            // incoming state setup
            switch (this._watchState) {
                case 'WAITING_ACTIVE':
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-waiting');
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-active');
                    break;
                case 'ACTIVE_LOCK':
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-active');
                    break;
                case 'ACTIVE_ERROR':
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-waiting');
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-active-error');
                    break;
                case 'BACKGROUND':
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-background');
                    break;
                case 'BACKGROUND_ERROR':
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-waiting');
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-background-error');
                    break;
                case 'OFF':
                    break;
                default:
                    assert(false, `Unexpected watchState ${this._watchState}`);
            }

            // manage geolocation.watchPosition / geolocation.clearWatch
            if (this._watchState === 'OFF' && this._geolocationWatchID !== undefined) {
                // clear watchPosition as we've changed to an OFF state
                this._clearWatch();
            } else if (this._geolocationWatchID === undefined) {
                // enable watchPosition since watchState is not OFF and there is no watchPosition already running

                this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-waiting');
                this._geolocateButton.setAttribute('aria-pressed', 'true');

                this._geolocationWatchID = window.navigator.geolocation.watchPosition(
                    this._onSuccess,
                    this._onError,
                    this.options.positionOptions
                );
            }
        } else {
            window.navigator.geolocation.getCurrentPosition(
                this._onSuccess,
                this._onError,
                this.options.positionOptions
            );

            // This timeout ensures that we still call finish() even if
            // the user declines to share their location in Firefox
            this._timeoutId = setTimeout(this._finish, 10000 /* 10sec */);
        }

        return true;
    }

    public onDeviceOrientationUpdated(event: any) {
        // Ignore callback if control is inactive
        if (!this.isActive) {
            return;
        }

        let heading = -event.alpha;

        // Absolute compass orientation on iOS devices
        if (event.webkitCompassHeading) {
            heading = event.webkitCompassHeading;
        }

        if (!heading) return;

        // Only update heading every 200ms
        const isLastUpdateAWhileAgo = Date.now() - this.lastUpdate > 200;
        const isRelevantAngleChange = Math.abs(this.lastHeading - heading) > 2;
        if (isRelevantAngleChange && isLastUpdateAWhileAgo) {
            this.lastHeading = heading;
            this.locationFeature.properties.heading = this.lastHeading;
            this._updateUserPosition();
            this.lastUpdate = Date.now();
        }
    }

    private _onSuccess(position: Position) {
        // Clear watch if map was garbage collected
        if (!this._map) {
            this._clearWatch();
            return;
        }

        // Ignore callback if control is inactive
        if (!this.isActive) {
            return;
        }

        if (this.options.trackUserLocation) {
            // keep a record of the position so that if the state is BACKGROUND and the user
            // clicks the button, we can move to ACTIVE_LOCK immediately without waiting for
            // watchPosition to trigger _onSuccess
            this._lastKnownPosition = position;

            switch (this._watchState) {
                case 'WAITING_ACTIVE':
                case 'ACTIVE_LOCK':
                case 'ACTIVE_ERROR':
                    this._watchState = 'ACTIVE_LOCK';
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-waiting');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-active-error');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background');
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-active');
                    break;
                case 'BACKGROUND':
                case 'BACKGROUND_ERROR':
                    this._watchState = 'BACKGROUND';
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-waiting');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background-error');
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-background');
                    break;
                default:
                    assert(false, `Unexpected watchState ${this._watchState}`);
            }
        }

        // if showUserLocation and the watch state isn't off then update the marker location
        if (this.options.showUserLocation && this._watchState !== 'OFF') {
            this._updateMarker(position);
        }

        // Optional ignore flag on inital trigger calls
        if (!this.cameraUpdateDisabled) {
            // if in normal mode (not watch mode), or if in watch mode and the state is active watch
            // then update the camera
            if (!this.options.trackUserLocation || this._watchState === 'ACTIVE_LOCK') {
                this._updateCamera(position);
            }
        }

        this._finish();
    }

    private _onError(error: PositionError) {
        // Ignore callback if control is inactive
        if (!this.isActive) {
            return;
        }

        if (this.options.trackUserLocation) {
            if (error.code === 1) {
                // PERMISSION_DENIED
                this._watchState = 'OFF';
                this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-waiting');
                this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-active');
                this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-active-error');
                this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background');
                this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background-error');

                if (this._geolocationWatchID !== undefined) {
                    this._clearWatch();
                }
            } else {
                switch (this._watchState) {
                    case 'WAITING_ACTIVE':
                        this._watchState = 'ACTIVE_ERROR';
                        this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-active');
                        this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-active-error');
                        break;
                    case 'ACTIVE_LOCK':
                        this._watchState = 'ACTIVE_ERROR';
                        this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-active');
                        this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-active-error');
                        this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-waiting');
                        // turn marker grey
                        break;
                    case 'BACKGROUND':
                        this._watchState = 'BACKGROUND_ERROR';
                        this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background');
                        this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-background-error');
                        this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-waiting');
                        // turn marker grey
                        break;
                    case 'ACTIVE_ERROR':
                        break;
                    default:
                        assert(false, `Unexpected watchState ${this._watchState}`);
                }
            }
        }

        this._finish();
    }

    private _updateCamera(position: Position) {
        const center = new LngLat(position.coords.longitude, position.coords.latitude);
        const radius = position.coords.accuracy;
        const bearing = this._map.getBearing();
        const options = Object.assign({ bearing }, this.options.fitBoundsOptions);

        // Ignore updates if not in maxBounds
        const maxBounds = this._map.getMaxBounds();
        if (maxBounds) {
            const isInBox =
                center.lat < maxBounds.getNorth() &&
                center.lng < maxBounds.getEast() &&
                center.lat > maxBounds.getSouth() &&
                center.lng > maxBounds.getWest();

            if (!isInBox) return;
        }

        this._map.fitBounds(center.toBounds(radius), options, {
            geolocateSource: true // tag this camera change so it won't cause the control to change to background state
        });
    }

    private _finish() {
        if (this._timeoutId) {
            clearTimeout(this._timeoutId);
        }
        this._timeoutId = undefined;
    }

    private _addPositionDataSource() {
        this.locationPoint = {
            type: 'Point',
            coordinates: [0, 0]
        };
        this.locationFeature = {
            type: 'Feature',
            geometry: this.locationPoint,
            properties: {
                visible: false,
                heading: 0,
                accuracy: 0
            }
        };
        this.locationSource = {
            type: 'FeatureCollection',
            features: [this.locationFeature]
        };
    }

    private _addPositionLayer(map: Map) {
        map.addSource('userlocation', { type: 'geojson', data: this.locationSource });

        map.loadImage('/img/map/orientation.png', (error, image) => {
            if (error) {
                console.error(error);
            } else {
                map.addImage('orientation', image);
            }
        });

        // array with magic values. keep it like that
        map.addLayer({
            id: 'userPositionAccuracy',
            type: 'circle',
            source: 'userlocation',
            paint: {
                'circle-color': '#E32D3D',
                'circle-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    10, ['/', ['get', 'accuracy'], 52.8],
                    11, ['/', ['get', 'accuracy'], 26.4],
                    12, ['/', ['get', 'accuracy'], 13.2],
                    13, ['/', ['get', 'accuracy'], 6.6],
                    14, ['/', ['get', 'accuracy'], 3.3],
                    15, ['/', ['get', 'accuracy'], 1.65],
                    16, ['/', ['get', 'accuracy'], 0.825],
                    17, ['/', ['get', 'accuracy'], 0.4125],
                    18, ['/', ['get', 'accuracy'], 0.20625]
                ],
                'circle-opacity': 0.15,
                'circle-pitch-alignment': 'map'
            },
            filter: ['get', 'visible']
        });

        map.addLayer({
            id: 'points',
            type: 'symbol',
            source: 'userlocation',
            layout: {
                'icon-image': 'orientation',
                'icon-size': 0.2,
                'icon-rotate': ['get', 'heading'],
                'icon-rotation-alignment': 'map',
                'icon-pitch-alignment': 'map',
                'icon-allow-overlap': true,
                'icon-ignore-placement': true
            },
            filter: ['get', 'visible']
        });

        map.addLayer({
            id: 'userPosition',
            type: 'circle',
            source: 'userlocation',
            paint: {
                'circle-color': '#E32D3D',
                'circle-radius': 6,
                'circle-opacity': 1,
                'circle-stroke-width': 1,
                'circle-stroke-color': '#E32D3D',
                'circle-stroke-opacity': 0.25,
                'circle-pitch-alignment': 'map'
            },
            filter: ['get', 'visible']
        });
    }

    private _updateMarker(position?: Position) {
        if (position) {
            if (this.locationFeature) {
                this.locationFeature.properties.visible = true;
                this.locationFeature.properties.heading = this.lastHeading;
                this.locationFeature.properties.accuracy = position.coords.accuracy;
            }

            if (this.locationPoint) {
                this.locationPoint.coordinates = [position.coords.longitude, position.coords.latitude];
            }
        } else if (this.locationFeature) {
            this.locationFeature.properties.visible = false;
        }

        this._updateUserPosition();
    }

    private _updateUserPosition() {
        if (!this._map) return;

        const userLocation = this._map.getSource('userlocation') as mapboxgl.GeoJSONSource;
        userLocation.setData(this.locationSource);
    }

    private _setupUI(supported: boolean) {
        if (supported === false) {
            console.warn('Geolocation support is not available, the GeolocateControl will not be visible.');
            return;
        }
        this._container.addEventListener('contextmenu', (e: MouseEvent) => e.preventDefault());
        this._geolocateButton = this._createElement(
            'button',
            `${className}-icon ${className}-geolocate no-ripple`,
            this._container
        );
        // @ts-ignore
        this._geolocateButton.type = 'button';
        this._geolocateButton.setAttribute('aria-label', 'Geolocate');

        if (this.options.trackUserLocation) {
            this._geolocateButton.setAttribute('aria-pressed', 'false');
            this._watchState = 'OFF';
        }

        // when showUserLocation is enabled, keep the Geolocate button disabled until
        // the device location marker is setup on the map
        if (this.options.showUserLocation) {
            // Data Source to keep track of current location
            this._addPositionDataSource();

            // User location layer
            this._addPositionLayer(this._map);

            if (this.options.trackUserLocation) this._watchState = 'OFF';
        }

        this._geolocateButton.addEventListener('click', this.trigger.bind(this));
        this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-background');

        // when the camera is changed (and it's not as a result of the Geolocation Control) change
        // the watch mode to background watch, so that the marker is updated but not the camera.
        if (this.options.trackUserLocation) {
            this._map.on('movestart', event => {
                if (!event.geolocateSource && this._watchState === 'ACTIVE_LOCK') {
                    this._watchState = 'BACKGROUND';
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-background');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-active');
                }
            });
        }
    }

    public stop() {
        this._clearWatch();
    }

    private _clearWatch() {
        window.navigator.geolocation.clearWatch(this._geolocationWatchID);

        this._geolocationWatchID = undefined;
        this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-waiting');
        this._geolocateButton.setAttribute('aria-pressed', 'false');

        if (this.options.showUserLocation) {
            this._updateMarker(null);
        }
    }

    private _createElement(tagName: string, classes?: string, container?: HTMLElement) {
        const el = window.document.createElement(tagName);
        if (classes) el.className = classes;
        if (container) container.appendChild(el);
        return el;
    }

    private _removeElement(node: HTMLElement) {
        if (node.parentNode) {
            node.parentNode.removeChild(node);
        }
    }
}
